import {
  AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { Point, defaultVanishingPoint, drawNeuralScene } from './neural-scene';

interface Particle {
  x: number; y: number; vx: number; vy: number;
  radius: number; life: number; duration: number; fire: boolean;
}

const IDLE_SPEED = 2.2;

@Component({
  selector: 'app-login-scene',
  standalone: true,
  imports: [TranslocoModule],
  templateUrl: './login-scene.component.html',
  styleUrl: './login-scene.component.css',
})
export class LoginSceneComponent implements AfterViewInit, OnDestroy {
  @ViewChild('landscape', { static: true }) private landscapeRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('effects', { static: true }) private effectsRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('carWrap', { static: true }) private carWrapRef!: ElementRef<HTMLDivElement>;

  private readonly host: HTMLElement;
  private readonly reducePreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  private readonly mobilePreference = window.matchMedia('(max-width: 900px)');
  private readonly skyImage = new Image();
  private readonly particles: Particle[] = [];
  private readonly timers = new Set<number>();
  private landscapeCtx!: CanvasRenderingContext2D;
  private effectsCtx!: CanvasRenderingContext2D;
  private width = 1;
  private height = 1;
  private dpr = 1;
  private time = 0;
  private distance = 0;
  private speed = IDLE_SPEED;
  private targetSpeed = IDLE_SPEED;
  private still = this.reducePreference.matches || this.mobilePreference.matches;
  private raf: number | null = null;
  private last = 0;
  private smokeAcc = 0;
  private launchActive = false;
  private popActive = false;
  private launched = false;
  private twoStepActive = false;
  private twoStepTimer: number | null = null;
  private flashTimer: number | null = null;
  private resizeObserver?: ResizeObserver;
  private vanishing: Point | null = null;

  constructor(hostRef: ElementRef<HTMLElement>, private readonly zone: NgZone) {
    this.host = hostRef.nativeElement;
  }

  private readonly onVisibilityChange = () => this.syncMotion();
  private readonly onMotionChange = () => {
    this.still = this.reducePreference.matches || this.mobilePreference.matches;
    this.clearAction();
    this.syncMotion();
  };

  ngAfterViewInit(): void {
    this.landscapeCtx = this.landscapeRef.nativeElement.getContext('2d')!;
    this.effectsCtx = this.effectsRef.nativeElement.getContext('2d')!;
    this.zone.runOutsideAngular(() => {
      this.skyImage.onload = () => this.draw(0);
      this.skyImage.src = 'assets/img/login/neural-circuit-sky.webp';
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.reducePreference.addEventListener('change', this.onMotionChange);
      this.mobilePreference.addEventListener('change', this.onMotionChange);
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.host);
      this.resize();
      this.syncMotion();
    });
  }

  ngOnDestroy(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.clearTimers();
    this.resizeObserver?.disconnect();
    this.skyImage.onload = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.reducePreference.removeEventListener('change', this.onMotionChange);
    this.mobilePreference.removeEventListener('change', this.onMotionChange);
  }

  pop(): void {
    if (this.popActive || this.launchActive || this.still) return;
    this.popActive = true;
    this.flash();
    this.emit(26, true);
    this.later(() => this.emit(20, true), 140);
    this.later(() => { this.popActive = false; }, 470);
  }

  onCarPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.startTwoStep();
  }

  onCarKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (!event.repeat) this.startTwoStep();
  }

  onCarKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') this.stopTwoStep();
  }

  stopTwoStep(): void {
    this.twoStepActive = false;
    this.cancel(this.twoStepTimer);
    this.twoStepTimer = null;
  }

  private startTwoStep(): void {
    if (this.twoStepActive || this.launchActive || this.still) return;
    this.twoStepActive = true;
    this.pop();
    this.scheduleTwoStep(180);
  }

  private scheduleTwoStep(delay: number): void {
    this.twoStepTimer = this.later(() => {
      this.twoStepTimer = null;
      if (!this.twoStepActive || this.launchActive) return;
      const roll = Math.random();
      if (roll < .15) {
        this.emit(34 + Math.round(Math.random() * 10), true, 1.6 + Math.random() * .6);
        this.flash();
      } else if (roll < .4) {
        this.emit(24 + Math.round(Math.random() * 8), true);
        this.flash();
      } else {
        this.emit(6 + Math.round(Math.random() * 6), true);
      }
      this.scheduleTwoStep(55 + Math.random() * 85);
    }, delay);
  }

  private flash(): void {
    this.cancel(this.flashTimer);
    this.host.classList.remove('backfire');
    void this.host.offsetWidth;
    this.host.classList.add('backfire');
    this.flashTimer = this.later(() => {
      this.flashTimer = null;
      this.host.classList.remove('backfire');
    }, 470);
  }

  launch(): void {
    if (this.launched || this.still) return;
    this.launched = true;
    this.clearAction();
    const carWrap = this.carWrapRef.nativeElement;
    const box = carWrap.getBoundingClientRect();
    const parent = this.host.getBoundingClientRect();
    const target = this.vanishing ?? defaultVanishingPoint(this.width, this.height, this.mobilePreference.matches);
    carWrap.style.setProperty('--launch-x', `${target.x - (box.left - parent.left + box.width * .5)}px`);
    carWrap.style.setProperty('--launch-y', `${target.y - (box.top - parent.top + box.height * .70)}px`);
    this.pop();
    this.launchActive = true;
    this.targetSpeed = 14;
    this.later(() => this.host.classList.add('launching'), 150);
  }

  reset(): void {
    if (!this.launched) return;
    this.clearTimers();
    this.popActive = false;
    this.host.classList.remove('backfire', 'launching');
    const carWrap = this.carWrapRef.nativeElement;
    carWrap.style.transition = 'none';
    carWrap.style.opacity = '0';
    this.targetSpeed = IDLE_SPEED;
    this.later(() => {
      carWrap.style.transition = 'opacity .65s';
      carWrap.style.opacity = '1';
      this.later(() => {
        carWrap.style.transition = '';
        carWrap.style.opacity = '';
        this.launchActive = false;
        this.launched = false;
        this.particles.length = 0;
      }, 700);
    }, 80);
  }

  private later(callback: () => void, delay: number): number {
    return this.zone.runOutsideAngular(() => {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        callback();
      }, delay);
      this.timers.add(id);
      return id;
    });
  }

  private cancel(id: number | null): void {
    if (id === null) return;
    clearTimeout(id);
    this.timers.delete(id);
  }

  private clearTimers(): void {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.twoStepTimer = this.flashTimer = null;
  }

  private clearAction(): void {
    this.clearTimers();
    this.launchActive = this.popActive = this.twoStepActive = false;
    this.targetSpeed = IDLE_SPEED;
    this.particles.length = 0;
    this.host.classList.remove('launching', 'backfire');
  }

  private resize(): void {
    this.width = this.host.clientWidth;
    this.height = this.host.clientHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    for (const canvas of [this.landscapeRef.nativeElement, this.effectsRef.nativeElement]) {
      canvas.width = Math.round(this.width * this.dpr);
      canvas.height = Math.round(this.height * this.dpr);
    }
    this.vanishing = this.mobilePreference.matches ? null : this.carVanishingPoint();
    this.draw(0);
  }

  private carVanishingPoint(): Point | null {
    const mark = this.carWrapRef.nativeElement.querySelector('.vanishing-mark');
    if (!mark) return null;
    const box = mark.getBoundingClientRect();
    const parent = this.host.getBoundingClientRect();
    return { x: box.left - parent.left, y: box.top - parent.top };
  }

  private exhaust(): { x: number; y: number }[] {
    const parent = this.host.getBoundingClientRect();
    const ports = this.carWrapRef.nativeElement.querySelectorAll('.exhaust-port');
    return Array.from(ports, port => {
      const box = port.getBoundingClientRect();
      return { x: box.left - parent.left, y: box.top - parent.top };
    });
  }

  private emit(count: number, fire: boolean, power = 1): void {
    if (this.still) return;
    const origins = this.exhaust();
    for (let i = 0; i < count; i++) {
      const origin = origins[i % origins.length];
      this.particles.push({
        x: origin.x + Math.random() * 18 - 9,
        y: origin.y + Math.random() * 8,
        vx: fire ? (110 + Math.random() * 100) * power : 20 + Math.random() * 30,
        vy: (fire ? 35 : -18) + Math.random() * 25,
        radius: fire ? (3 + Math.random() * 7) * power : 7 + Math.random() * 9,
        life: 0,
        duration: fire ? (.22 + Math.random() * .3) * (1 + (power - 1) * .5) : .8 + Math.random() * .7,
        fire,
      });
    }
    if (this.particles.length > 180) this.particles.splice(0, this.particles.length - 180);
  }

  private drawParticles(dt: number): void {
    const fx = this.effectsCtx;
    fx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    fx.clearRect(0, 0, this.width, this.height);
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      particle.life += dt;
      if (particle.life >= particle.duration) {
        this.particles.splice(i, 1);
        continue;
      }
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      const alpha = 1 - particle.life / particle.duration;
      fx.beginPath();
      fx.arc(particle.x, particle.y, particle.radius * (particle.fire ? 1 : 1 + particle.life), 0, Math.PI * 2);
      fx.fillStyle = particle.fire
        ? `rgba(255,${Math.round(100 + alpha * 130)},${Math.round(alpha * 110)},${alpha})`
        : `rgba(185,194,203,${alpha * .12})`;
      fx.fill();
    }
  }

  private draw(dt: number): void {
    if (!this.landscapeCtx) return;
    this.landscapeCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    drawNeuralScene(this.landscapeCtx, {
      width: this.width,
      height: this.height,
      time: this.time,
      distance: this.distance,
      mobile: this.mobilePreference.matches,
      reduced: this.still,
      skyImage: this.skyImage,
      vanishing: this.vanishing,
    });
    this.drawParticles(dt);
  }

  private readonly frame = (now: number): void => {
    this.raf = null;
    if (this.still || document.hidden) return;
    if (this.last && now - this.last < 1000 / 60 - 1) {
      this.raf = requestAnimationFrame(this.frame);
      return;
    }
    const dt = this.last ? Math.min((now - this.last) / 1000, .05) : 0;
    this.last = now;
    this.time += dt;
    this.speed += (this.targetSpeed - this.speed) * (1 - Math.exp(-dt * 3));
    this.distance += this.speed * dt;
    this.smokeAcc += dt;
    if (this.smokeAcc > .14 && !this.launchActive) {
      this.emit(2, false);
      this.smokeAcc = 0;
    }
    this.draw(dt);
    this.raf = requestAnimationFrame(this.frame);
  };

  private syncMotion(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.last = 0;
    this.host.classList.toggle('is-paused', this.still);
    if (!this.still && !document.hidden) this.raf = requestAnimationFrame(this.frame);
    else this.draw(0);
  }
}
